import { SecondaryButton } from '@components/Buttons/SecondaryButton/SecondaryButton'
import Text from '@components/Text/Text'
import { TextTag } from '@enums/textTag'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTypedSelector } from '../../store'
import './ErrorCodeModal.scss'
import { useDispatch } from 'react-redux'
import { fetchMenu } from '@actions/menuActions'
import {
  exitTizenApplication,
  exitWebOSApplication,
  getActiveProfile,
  getErrorCode,
  onEnterKeyPressAndClick,
} from '@utils/helpers'
import DotSpinner from '@components/DotSpinner/DotSpinner'
import { routes } from '@constants/routes.const'
import packageJSON from '../../../package.json'
import { fetchMovieDetails } from '@actions/movieDetailActions'
import {
  ErrorActions,
  ErrorCodes,
  errorScreenSpinnerTimeout,
  exitAppErrorActions,
  Language,
  SessionStorageItem,
} from '@constants/index'
import { setError } from '@actions/errorCodeActions'
import { fetchPage } from '@actions/pageActions'
import { categoryType } from '@constants/category.const'
import { VelvetSDK } from 'sdk'
import useNavigationHistory from '@hooks/useNavigationHistory'

interface ErrorCodeModalProps {
  showMenu?: (status: boolean) => void
  isOffline?: boolean
}

const ErrorCodeModal = ({ showMenu, isOffline }: ErrorCodeModalProps) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const tryAgainButtonRef = useRef<HTMLButtonElement>(null)
  const [isLoading, setIsLoading] = useState(false)
  const V = VelvetSDK.Vocabulary.vocabulary
  const errorCode = useTypedSelector((state) => state.errorCode?.errorCode)
  const errorHTTPcode = useTypedSelector((state) => state.errorCode?.HTTPcode)
  const errorAction = useTypedSelector((state) => state.errorCode?.fetchAction)
  const errorCategory = useTypedSelector((state) => state.errorCode?.category)
  const errorMovieId = useTypedSelector((state) => state.errorCode?.movieId)
  const errorVideoSlug = useTypedSelector((state) => state.errorCode?.videoSlug)
  const menuContent = useTypedSelector((state) => state.menu?.data?.mainMenu?.content)
  const showErrorModal = useTypedSelector((state) => state.errorCode?.showErrorModal)
  const errorPageSlug = useTypedSelector((state) => state.errorCode?.feedPageSlug)
  const errorSubscriptionPlan = useTypedSelector((state) => state.errorCode?.feedSubscriptionPlan)
  const prevHomeRoute = JSON.parse(
    sessionStorage.getItem(SessionStorageItem.LAST_FOCUSED_ITEM_HOME) as string,
  )?.homeSlug
  const exitApp = exitAppErrorActions.includes(errorAction)
  const labelSomethingWentWrong = V?.errorPageGenericTitle ?? 'Something went wrong'
  const labelPleaseTryAgain = V?.errorPageGenericDescription ?? 'Please try again later.'
  const labelNoNetwork = 'Please try again and restart your home network and your streaming device.'
  const labelCode = V?.errorPageCodeLabel ?? 'Code:'
  const labelCouldNotConnectToVelvet = 'Couldn’t connect to Velvet'
  const tryAgainButtonLabel = V?.tryAgainButtonLabel ?? 'Try Again'
  const goBackButtonLabel = V?.goBackButtonLabel ?? 'Go Back'
  const exitButtonLabel = V?.errorPageExitButtonLabel ?? 'Exit'
  const errorMessageText = isOffline
    ? labelCouldNotConnectToVelvet
    : errorAction === ErrorActions.CREATE_PROFILE
    ? V?.errorLabelProfileCreate
    : errorAction === ErrorActions.DELETE_PROFILE
    ? V?.errorLabelProfileDelete
    : labelSomethingWentWrong
  const errorTryAgainMessageText = isOffline ? labelNoNetwork : labelPleaseTryAgain
  const navigateBackFromPlayer = sessionStorage.getItem('navigateBackFromPlayer')
  const { safeGoBack } = useNavigationHistory()
  const activeProfile = getActiveProfile()

  const navigateBack = useCallback(
    (e: React.KeyboardEvent | React.MouseEvent) => {
      onEnterKeyPressAndClick(e, () => {
        switch (true) {
          case errorAction?.includes(ErrorActions.CATEGORY):
            dispatch(setError({ showErrorModal: false }))
            navigate(prevHomeRoute ?? -1)
            break
          case errorAction === ErrorActions.DETAILS:
            if (errorCategory === ErrorActions.FEED) {
              dispatch(setError({ showErrorModal: false }))
              navigate(prevHomeRoute ?? -1)
            } else if (errorCategory === categoryType.MOVIE || errorCategory === categoryType.SERIE) {
              dispatch(setError({ showErrorModal: false }))
              safeGoBack()
            }
            break
          case errorAction === ErrorActions.ENTITLEMENT:
            dispatch(setError({ showErrorModal: false }))
            navigateBackFromPlayer ? navigate(navigateBackFromPlayer) : safeGoBack()
           
            break
          default:
            dispatch(setError({ showErrorModal: false }))
            navigate(-1)
            break
        }
      })
    },
    [errorAction, dispatch, navigate, prevHomeRoute, errorCategory, navigateBackFromPlayer, safeGoBack],
  )

  const configService = useCallback(async () => {
    if (process.env.REACT_APP_BASE_URL) {
      await VelvetSDK.Configuration.service.fetchConfiguration(process.env.REACT_APP_BASE_URL)
    }
  }, [])

  const fetchConfig = useCallback(async () => {
    await configService()

    if (VelvetSDK.Configuration.configuration.error) {
      const error = VelvetSDK.Configuration.configuration.error

      const errorUpdate = {
        showErrorModal: true,
        errorCode: ErrorCodes.FETCH_CONFIG,
        HTTPcode: error.status,
        fetchAction: ErrorActions.CONFIG,
      }

      dispatch(setError(errorUpdate))
    } else {
      if (errorAction === ErrorActions.CONFIG) {
        dispatch(setError({ errorCode: '', showErrorModal: false }))
      }
    }
  }, [configService, dispatch, errorAction])

  const fetchVocabulary = useCallback(
    async (language?: string | null) => {
      await VelvetSDK.Vocabulary.service.fetchVocabulary(language)

      if (VelvetSDK.Vocabulary.vocabulary?.error) {
        const error = VelvetSDK.Vocabulary.vocabulary.error
        const errorUpdate = {
          showErrorModal: true,
          errorCode: ErrorCodes.FETCH_VOCABULARY,
          HTTPcode: error.status,
          fetchAction: ErrorActions.VOCABULARY,
        }

        dispatch(setError(errorUpdate))
      }
    },
    [dispatch],
  )

  const tryAgainRefetching = useCallback(() => {
    setIsLoading(true)

    setTimeout(() => {
      switch (true) {
        case errorAction === ErrorActions.CONFIG:
          fetchConfig()
          break
        case errorAction === ErrorActions.MENU:
          dispatch(fetchMenu(activeProfile?.preferredLanguage))
          break
        case errorAction === ErrorActions.VOCABULARY:
          fetchVocabulary(activeProfile?.preferredLanguage ?? Language.EN)
          break
        case errorAction === ErrorActions.FEED:
          dispatch(fetchPage(errorPageSlug, errorSubscriptionPlan, activeProfile?.preferredLanguage))
          break
        case errorAction === ErrorActions.DETAILS:
          dispatch(fetchMovieDetails(errorCategory, errorMovieId, activeProfile?.preferredLanguage))
          break
        case errorAction?.includes(ErrorActions.CATEGORY):
          navigate(`${routes.CATEGORY}/${errorAction.split('/')[1]}`)
          break
        case errorAction === ErrorActions.ENTITLEMENT:
          navigate(`${routes.PLAYER}/${errorVideoSlug}`)
          break
        case errorAction === ErrorActions.CREATE_PROFILE:
        case errorAction === ErrorActions.DELETE_PROFILE:
          navigate(routes.MANAGE_PROFILES)
          break
        case isOffline:
          navigate(`${menuContent[0].callToActions.webUrl}`)
          break
      }

      setIsLoading(false)
    }, errorScreenSpinnerTimeout)
  }, [
    dispatch,
    errorAction,
    errorCategory,
    errorMovieId,
    errorPageSlug,
    errorSubscriptionPlan,
    errorVideoSlug,
    fetchConfig,
    fetchVocabulary,
    isOffline,
    activeProfile?.preferredLanguage,
    menuContent,
    navigate,
  ])

  const exitTheApp = useCallback(() => {
    exitTizenApplication()
    exitWebOSApplication()
  }, [])

  useEffect(() => {
    showMenu && showMenu(false)
    tryAgainButtonRef.current?.focus()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showMenu, tryAgainButtonRef.current])

  return (
    <>
      {isLoading ? (
        <DotSpinner />
      ) : (
        <>
          <div className="error__page">
            <div className="error__wrapper">
              <div className="error__messages">
                <Text tag={TextTag.P} className="error__message">
                  {errorMessageText}
                </Text>
                <Text tag={TextTag.P} className={isOffline ? 'offline-msg' : 'try-again__message'}>
                  {errorTryAgainMessageText}
                </Text>
                {!isOffline &&
                  errorAction !== ErrorActions.CREATE_PROFILE &&
                  errorAction !== ErrorActions.DELETE_PROFILE && (
                    <Text tag={TextTag.P} className="error__code">
                      {labelCode} {getErrorCode(errorCode, errorHTTPcode, packageJSON.version)}
                    </Text>
                  )}
              </div>
            </div>
            <div className="error__button-container">
              <SecondaryButton
                onClick={tryAgainRefetching}
                onFocusUp={false}
                buttonRef={tryAgainButtonRef}
                className="try-again"
              >
                {tryAgainButtonLabel}
              </SecondaryButton>
              {exitApp || isOffline ? (
                <SecondaryButton onClick={exitTheApp} className="exit" onFocusDown={showErrorModal && true}>
                  {exitButtonLabel}
                </SecondaryButton>
              ) : (
                errorAction !== ErrorActions.CREATE_PROFILE &&
                errorAction !== ErrorActions.DELETE_PROFILE && (
                  <SecondaryButton onClick={navigateBack} className="go-back" onFocusDown={showErrorModal && true}>
                    {goBackButtonLabel}
                  </SecondaryButton>
                )
              )}
            </div>
          </div>
        </>
      )}
    </>
  )
}
export default ErrorCodeModal
